'use client';

import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { motion } from 'framer-motion';

interface PerformanceData {
  time: string;
  value: number;
}

interface AllocationData {
  name: string;
  value: number;
  color: string;
}

export function PortfolioPerformanceChart({ data }: { data: PerformanceData[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full min-h-[300px] flex flex-col items-center justify-center border border-dashed border-white/5 rounded-3xl bg-white/[0.01] gap-4">
        <div className="w-8 h-8 rounded-full border-2 border-dashed border-brand-teal/30 animate-spin" />
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary">Neural Intercept Pending</span>
      </div>
    );
  }
  return (
    <div className="w-full h-full min-h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00FFDE" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#00FFDE" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
          <XAxis 
            dataKey="time" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
            dy={10}
          />
          <YAxis 
            hide 
            domain={['auto', 'auto']}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'rgba(15, 15, 15, 0.9)', 
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              backdropFilter: 'blur(10px)',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)'
            }}
            itemStyle={{ color: '#00FFDE' }}
          />
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke="#00FFDE" 
            strokeWidth={2}
            fillOpacity={1} 
            fill="url(#colorValue)" 
            isAnimationActive={true}
            animationDuration={2000}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AssetAllocationChart({ data }: { data: AllocationData[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full min-h-[300px] flex items-center justify-center border border-dashed border-white/5 rounded-full bg-white/[0.01]">
         <span className="text-[10px] font-black uppercase tracking-widest text-text-tertiary opacity-40">Unallocated</span>
      </div>
    );
  }
  return (
    <div className="w-full h-full min-h-[300px] flex items-center justify-center">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
            isAnimationActive={true}
            animationBegin={500}
            animationDuration={1500}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
            ))}
          </Pie>
          <Tooltip 
             contentStyle={{ 
              backgroundColor: 'rgba(15, 15, 15, 0.9)', 
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              fontSize: '11px'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      
      {/* Visual Depth: Center Text Overlay */}
      <div className="absolute flex flex-col items-center">
         <span className="text-[10px] font-black uppercase tracking-widest text-text-tertiary">Exposure</span>
         <span className="text-lg font-bold text-text-primary tracking-tighter">100%</span>
      </div>
    </div>
  );
}
